import type { Asset } from '../types'
import { API_BASE, authHeaders } from '../services/apiAuth'
import { lookOf, readingSize } from './signature'

/**
 * Finishing a picture brought in from Amazon or Google (Huanrui, 2026-11-02)
 * -------------------------------------------------------------
 * Those pictures go into Audit from the Worker's queue (externalToLibrary.ts),
 * which cannot read pixels. So the first browser that shows one in a library
 * grid does the two things that need them, and hands the result back:
 *
 *   - its look (signature.js, the same reading Image Tag, the Egnyte importer
 *     and the backfill make), so the Review Queue hears about a picture that
 *     looks like one already in a library;
 *   - a 600 px WebP thumbnail, the size Image Tag makes, so the grid stops
 *     loading the whole picture.
 *
 * Quietly, two at a time, once per picture per page load; a failure is left
 * for the next browser that shows it. The Worker takes an answer only while
 * the picture has none (finishExternal there).
 */

const THUMBNAIL_EDGE = 600
const tried = new Set<string>()
const waiting: Asset[] = []
let running = 0

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * A picture still waiting for its look: a library row brought in from Amazon
 * or Google, its look still empty. A row read from the library says so with
 * null; a card the External Library draws for a search result has no such
 * field at all, nor an id of the library's kind, and is left alone.
 */
export function needsFinishing(asset: Asset): boolean {
  return (
    asset.source === 'external' &&
    asset.perceptualHash === null &&
    UUID.test(asset.id) &&
    Boolean(asset.createdAt) &&
    !tried.has(asset.id)
  )
}

export function finishLater(assets: Asset[]): void {
  for (const asset of assets) {
    if (!needsFinishing(asset)) continue
    tried.add(asset.id)
    waiting.push(asset)
  }
  pump()
}

function pump(): void {
  while (running < 2 && waiting.length > 0) {
    const next = waiting.shift()!
    running += 1
    void finish(next)
      .catch((error: unknown) => console.warn('an Amazon or Google picture could not be finished', next.id, error))
      .finally(() => {
        running -= 1
        pump()
      })
  }
}

function canvasOf(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

async function finish(asset: Asset): Promise<void> {
  const link = asset.thumbnailPath || asset.originalPath
  if (!/^https?:\/\//.test(link)) return
  const response = await fetch(link)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const bitmap = await createImageBitmap(await response.blob())
  try {
    const { width, height } = bitmap
    // The look, from the picture shrunk to its reading size, see-through parts on white.
    const size = readingSize(width, height)
    const small = canvasOf(size.width, size.height)
    const context = small.getContext('2d', { willReadFrequently: true })
    if (!context) return
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, size.width, size.height)
    context.imageSmoothingQuality = 'high'
    context.drawImage(bitmap, 0, 0, size.width, size.height)
    const look = lookOf(context.getImageData(0, 0, size.width, size.height).data, size.width, size.height)

    // A smaller copy for the grid, only when the picture is larger than one.
    let thumb = ''
    const longest = Math.max(width, height)
    if (longest > THUMBNAIL_EDGE) {
      const scale = THUMBNAIL_EDGE / longest
      const canvas = canvasOf(Math.round(width * scale), Math.round(height * scale))
      const drawing = canvas.getContext('2d')
      if (drawing) {
        drawing.imageSmoothingQuality = 'high'
        drawing.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.92))
        if (blob && blob.type === 'image/webp') thumb = await dataUrlOf(blob)
      }
    }

    const answer = await fetch(`${API_BASE}/api/ai/finish-external`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ assetId: asset.id, look, thumb }),
    })
    if (!answer.ok) throw new Error(`HTTP ${answer.status}`)
  } finally {
    bitmap.close()
  }
}

function dataUrlOf(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}
